import classNames from 'classnames';
import * as React from 'react';

import './door-image.style.scss';

import { useIntersection } from '../../../../../../new-providers';

import { DoorImageProps } from './door-image.props';

const DoorImage: React.FC<DoorImageProps> = function(props) {
  const {
          alt,
          productType,
          label,
          miniature,
          schema,
          side,
        } = props;

  const intersection = useIntersection();

  const imageRef   = React.useRef<HTMLImageElement | null>(null);
  const pictureRef = React.useRef<HTMLPictureElement | null>(null);

  const [isLoaded, setIsLoaded]             = React.useState(false);
  const [isIntersecting, setIsIntersecting] = React.useState(false);

  const itemProp = React.useMemo(() => {
    if (!schema) {
      return;
    }

    return 'image';
  }, [schema]);
  const sizes    = React.useMemo(() => {
    if (!isIntersecting) {
      return;
    }

    if (miniature) {
      return '320px';
    }

    return [
      '(max-width: 1439px) 320px',
      '(min-width: 1440px) and (max-width: 1679px) 1440px',
      '(min-width: 1680px) and (max-width: 1919px) 1680px',
      '1920px',
    ].join(', ');
  }, [miniature, isIntersecting]);
  const src      = React.useMemo(() => {
    if (!isIntersecting) {
      return;
    }

    if (miniature) {
      return `${props.src}.png?w=60&default=vertical&t=${BUILD_TIME}`;
    }

    return `${props.src}.png?w=744&default=vertical&t=${BUILD_TIME}`;
  }, [miniature, props.src, isIntersecting]);
  const srcSet   = React.useMemo(() => {
    if (!isIntersecting) {
      return;
    }

    if (miniature) {
      return `${props.src}.png?w=60&default=vertical&t=${BUILD_TIME} 320w`;
    }

    return [
      `${props.src}.png?w=276&default=vertical&t=${BUILD_TIME} 320w`,
      `${props.src}.png?w=314&default=vertical&t=${BUILD_TIME} 1440w`,
      `${props.src}.png?w=352&default=vertical&t=${BUILD_TIME} 1680w`,
      `${props.src}.png?w=432&default=vertical&t=${BUILD_TIME} 1920w`,
    ].join(', ');
  }, [miniature, props.src, isIntersecting]);
  const title    = React.useMemo(() => {
    const parts = [productType, label];

    if (side) {
      parts.push(side);
    }

    return parts.join(' ');
  }, [productType, label, side]);
  const onLoad   = React.useMemo(() => {
    if (!isIntersecting) {
      return;
    }

    return () => {
      setIsLoaded(true);
    };
  }, [isIntersecting, sizes, src, srcSet]);

  React.useEffect(() => {
    let frameId: number;

    const element = pictureRef.current;

    frameId = requestAnimationFrame(() => {
      if (!isLoaded && element) {
        intersection.observe(element, (isIntersecting) => {
          if (isIntersecting) {
            setIsIntersecting(true);

            intersection.unobserve(element);
          }
        });
      }
    });

    return () => {
      cancelAnimationFrame(frameId);
      if (element) {
        intersection.unobserve(element);
      }
    };
  }, []);

  return (
    <div className={'door-image'}>
      {schema && (
        <>
          <meta
            content={`${props.src}.png?w=744&default=vertical&t=${BUILD_TIME}`}
            property={'og:image'}
          />
          <meta
            content={'744'}
            property={'og:image:width'}
          />
          <meta
            content={`${744 * 1.9467593}`}
            property={'og:image:height'}
          />
        </>
      )}
      <picture
        className={classNames({
          lazy: !isLoaded,
          miniature,
        })}
        ref={pictureRef}
      >
        {!isIntersecting
          ? null
          : miniature
            ? (
              <>
                <source
                  srcSet={`${props.src}.webp?w=60&default=vertical&t=${BUILD_TIME} 320w`}
                  type={'image/webp'}
                />
                <source
                  srcSet={`${props.src}.png?w=60&default=vertical&t=${BUILD_TIME} 320w`}
                  type={'image/png'}
                />
              </>
            )
            : (
              <>
                <source
                  media={'(max-width: 1439px)'}
                  srcSet={`${props.src}.webp?w=276&default=vertical&t=${BUILD_TIME}`}
                  type={'image/webp'}
                />
                <source
                  media={'(max-width: 1679px)'}
                  srcSet={`${props.src}.webp?w=314&default=vertical&t=${BUILD_TIME}`}
                  type={'image/webp'}
                />
                <source
                  media={'(max-width: 1919px)'}
                  srcSet={`${props.src}.webp?w=352&default=vertical&t=${BUILD_TIME}`}
                  type={'image/webp'}
                />
                <source
                  media={'(max-width: 2559px)'}
                  srcSet={`${props.src}.webp?w=432&default=vertical&t=${BUILD_TIME}`}
                  type={'image/webp'}
                />
                <source
                  media={'(min-width: 2560px)'}
                  srcSet={`${props.src}.webp?w=744&default=vertical&t=${BUILD_TIME}`}
                  type={'image/webp'}
                />
                <source
                  media={'(max-width: 1439px)'}
                  srcSet={`${props.src}.png?w=276&default=vertical&t=${BUILD_TIME}`}
                  type={'image/png'}
                />
                <source
                  media={'(max-width: 1679px)'}
                  srcSet={`${props.src}.png?w=314&default=vertical&t=${BUILD_TIME}`}
                  type={'image/png'}
                />
                <source
                  media={'(max-width: 1919px)'}
                  srcSet={`${props.src}.png?w=352&default=vertical&t=${BUILD_TIME}`}
                  type={'image/png'}
                />
                <source
                  media={'(max-width: 2559px)'}
                  srcSet={`${props.src}.png?w=432&default=vertical&t=${BUILD_TIME}`}
                  type={'image/png'}
                />
                <source
                  media={'(min-width: 2560px)'}
                  srcSet={`${props.src}.png?w=744&default=vertical&t=${BUILD_TIME}`}
                  type={'image/png'}
                />
              </>
            )
        }
        <img
          alt={alt}
          itemProp={itemProp}
          onContextMenu={(event) => {
            event.preventDefault();
          }}
          onLoad={onLoad}
          ref={imageRef}
          sizes={sizes}
          src={src}
          srcSet={srcSet}
          title={title}
        />
      </picture>
    </div>
  );
};

export default DoorImage;